import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { showNotification, showError } from '../utils/notificationHandler';

export default function AuthCallback() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const history = useHistory();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log('Processing auth callback, URL:', window.location.href);
        
        // Check for errors first
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        
        const errorDescription = urlParams.get('error_description') || hashParams.get('error_description') || 
                                urlParams.get('error') || hashParams.get('error');
        
        if (errorDescription) {
          throw new Error(errorDescription);
        }

        // Extract verification parameters from URL
        // Magic links can have parameters in hash or query string
        const token = urlParams.get('token') || hashParams.get('access_token');
        const tokenHash = urlParams.get('token_hash') || hashParams.get('token_hash');
        const type = urlParams.get('type') || hashParams.get('type') || 'magiclink';
        const email = urlParams.get('email') || hashParams.get('email');
        const refreshToken = hashParams.get('refresh_token');

        console.log('Extracted parameters:', { token: !!token, tokenHash: !!tokenHash, type, email: !!email, refreshToken: !!refreshToken });

        let authResult = null;

        // Try different verification methods based on available parameters
        if (token && type) {
          // Use verifyOtp for magic link tokens
          console.log('Attempting verifyOtp with token');
          authResult = await supabase.auth.verifyOtp({
            token_hash: tokenHash || token,
            type: type,
            email: email
          });
        } else if (token && refreshToken) {
          // Direct session setup with tokens from hash
          console.log('Attempting setSession with tokens');
          authResult = await supabase.auth.setSession({
            access_token: token,
            refresh_token: refreshToken
          });
        } else {
          // Fall back to automatic session detection
          console.log('Falling back to getSession');
          await new Promise(resolve => setTimeout(resolve, 1000));
          authResult = await supabase.auth.getSession();
        }

        if (authResult.error) {
          console.error('Authentication error:', authResult.error);
          throw authResult.error;
        }

        const session = authResult.data?.session;
        const user = authResult.data?.user || session?.user;

        if (session && user) {
          console.log('Authentication successful, user:', user.id);
          
          // Check if user exists in accounts table
          const { data: accountData, error: accountError } = await supabase
            .from('accounts')
            .select('id, password_set')
            .eq('id', user.id)
            .single();

          if (accountError) {
            console.error('Account lookup error:', accountError);
            throw new Error('Account not found. Please contact support.');
          }

          // Store account ID
          localStorage.setItem('accountId', accountData.id);
          
          // Clear any magic link tracking
          localStorage.removeItem('magicLinkSentTime');
          localStorage.removeItem('magicLinkEmail');

          showNotification('Successfully logged in via magic link!', 'success');
          
          // NOTE: password_set check removed - users now set passwords via recovery links
          // Legacy magic link invites can still redirect to /set-password if needed
          // but new onboarding flow uses /reset-password via recovery links
          
          // Check if user needs to set password (LEGACY - for old magic link invites only)
          if (!accountData.password_set) {
            console.log('User has not set password - redirecting to set-password (legacy magic link flow)');
            history.push('/set-password');
          } else {
            // Redirect to dashboard
            history.push('/user/dashboard');
          }
        } else {
          console.error('No session or user found after authentication');
          throw new Error('Magic link authentication failed - no session created');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        setError(error.message || 'Authentication failed');
        showError(error, 'Magic link authentication failed');
        
        // Redirect to login after a short delay
        setTimeout(() => {
          history.push('/login');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    handleAuthCallback();
  }, [history]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-dark">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand mx-auto"></div>
          <h2 className="mt-4 text-xl font-semibold text-brand">
            Authenticating...
          </h2>
          <p className="mt-2 text-gray-300">
            Processing your magic link authentication
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-dark">
        <div className="text-center max-w-md">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <h2 className="font-bold text-lg mb-2">Authentication Failed</h2>
            <p className="text-sm">{error}</p>
          </div>
          <p className="text-gray-300 text-sm">
            Redirecting to login page...
          </p>
        </div>
      </div>
    );
  }

  return null;
}
